import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import { pitruAcharyas, PitruAcharyaProfile } from '../../data/pitruAcharyas';
import { EXPERIMENT_ID } from '../../services/pitruLeadService';
import { capturePitruAttribution } from '../../utils/pitruAttribution';
import { trackEvent } from '../../utils/analytics';
import AcharyaNetwork from './AcharyaNetwork';
import { beforeBooking, completionEvidence, consultationLabel, consultationPath, consultationSupport, faqs, processSteps, uncertainties } from './content';
import './PitruLanding.css';

export default function PitruLanding({ profiles = pitruAcharyas }: { profiles?: readonly PitruAcharyaProfile[] }) {
  const viewed = useRef(false);
  const attribution = useRef(capturePitruAttribution());
  const eventContext = () => ({
    experiment_id: EXPERIMENT_ID, page_path: '/pitru-paksha',
    utm_source: attribution.current.source, utm_medium: attribution.current.medium,
    utm_campaign: attribution.current.campaignName, utm_content: attribution.current.content,
    utm_term: attribution.current.term
  });
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    const source = attribution.current;
    trackEvent('pp_v2_landing_view', 'pitru_paksha_v2', undefined, undefined, {
      experiment_id: EXPERIMENT_ID, page_path: '/pitru-paksha',
      utm_source: source.source, utm_medium: source.medium, utm_campaign: source.campaignName,
      utm_content: source.content, utm_term: source.term
    });
  }, []);
  const cta = (placement: string) => <div className="pp-landing__cta-group">
    <Link className="pp-landing__cta" to={consultationPath} onClick={() => trackEvent('pp_v2_consultation_cta_clicked', 'pitru_paksha_v2', placement, undefined, { ...eventContext(), cta_placement: placement })}>
      {consultationLabel}<span aria-hidden="true">↗</span>
    </Link>
    <p className="pp-landing__cta-support">{consultationSupport}</p>
  </div>;

  return <article className="pp-landing">
    <SEOHead title="Pitru Paksha for Families Abroad — Kashi & Gaya" canonicalPath="/pitru-paksha"
      description="Living abroad and arranging Pitru Paksha rites in India? Start with a free consultation about your family’s needs, timing and Kashi or Gaya arrangements."
      keywords="Pitru Paksha, NRI ancestral rites, families abroad, Kashi, Gaya, Shraddha consultation" />
    <section className="pp-landing__hero" aria-labelledby="pp-hero-title">
      <div className="pp-landing__wrap pp-landing__hero-grid">
        <div>
          <p className="pp-landing__eyebrow">Pitru Paksha · For families living abroad</p>
          <h1 id="pp-hero-title">Arrange your family’s Pitru Paksha rites in India, even if you live abroad</h1>
          <p className="pp-landing__hero-copy">Unsure about the right ritual, tithi, or whether Kashi or Gaya is appropriate? Speak with our Antaryatra team first and understand the path before you decide to book.</p>
          {cta('hero')}
          <p className="pp-landing__locations">Kashi &amp; Gaya arrangements available</p>
        </div>
        <aside className="pp-landing__promise" aria-label="Our approach">
          <div className="pp-landing__arch" aria-hidden="true"><span /><span /><span /></div>
          <p className="pp-landing__eyebrow">A conversation comes first</p>
          <h2>Understand first.<br /><em>Arrange with confidence.</em></h2>
          <div className="pp-landing__promise-line" />
          <p>Your family’s requirement.<br />A considered path.<br />Clarity before any payment.</p>
        </aside>
      </div>
    </section>

    <section className="pp-landing__section" aria-labelledby="pp-questions-title">
      <div className="pp-landing__wrap pp-landing__split">
        <div><p className="pp-landing__eyebrow">You can begin with uncertainty</p>
          <h2 id="pp-questions-title">You don’t need all the answers to take the first step.</h2>
          <p className="pp-landing__intro">You may be considering Shraddha, Pind Daan, Tarpan, Tripindi or other Pitru/Pitra-related rites. You do not need to know the correct ritual before contacting us. These are questions to discuss together.</p>
        </div>
        <ul className="pp-landing__questions">{uncertainties.map(question => <li key={question}><span aria-hidden="true">?</span>{question}</li>)}</ul>
      </div>
    </section>

    <section className="pp-landing__section pp-landing__tinted" aria-labelledby="pp-process-title">
      <div className="pp-landing__wrap">
        <p className="pp-landing__eyebrow">How Antaryatra works</p>
        <h2 id="pp-process-title">From your first question to a clear arrangement.</h2>
        <ol className="pp-landing__steps">{processSteps.map((step, index) => <li key={step.title}>
          <span className="pp-landing__step-number" aria-hidden="true">0{index + 1}</span>
          <h3>{step.title}</h3><p>{step.body}</p>
        </li>)}</ol>
        {cta('process')}
      </div>
    </section>

    <section className="pp-landing__section pp-landing__destination" aria-labelledby="pp-location-title">
      <div className="pp-landing__wrap pp-landing__split">
        <div><p className="pp-landing__eyebrow">Two sacred locations. Your family’s path.</p>
          <h2 id="pp-location-title">Kashi or Gaya? You don’t need to decide before speaking with us.</h2></div>
        <div><p className="pp-landing__intro">Different families may approach ancestral rites differently depending on their requirement, tradition, ritual and circumstances. The consultation is intended to help clarify the appropriate path.</p>
          <p className="pp-landing__location-options">Kashi <span aria-hidden="true">/</span> Gaya <span aria-hidden="true">/</span> Open to Guidance</p>
          <p>Bring a preference, or bring your questions. You are not choosing a package here.</p></div>
      </div>
    </section>

    <AcharyaNetwork profiles={profiles} />

    <section className="pp-landing__section pp-landing__tinted" aria-labelledby="pp-price-title">
      <div className="pp-landing__wrap pp-landing__split">
        <div><p className="pp-landing__eyebrow">Price clarity, before commitment</p>
          <h2 id="pp-price-title">Consultation is free.</h2>
          <p className="pp-landing__intro">Personalized family-specific arrangements generally fall in the ₹15,000–₹30,000 range depending on the ritual, location and scope. More elaborate arrangements may cost more.</p>
          <p>This is an indicative range only. You will know the recommended scope and total cost before making any payment.</p>
          {cta('pricing')}
        </div>
        <div className="pp-landing__before"><h3>Before you reserve, you will know:</h3>
          <ul>{beforeBooking.map(item => <li key={item}><span aria-hidden="true">✓</span>{item}</li>)}</ul>
          <p>We do not expect you to pay before the arrangement is understood.</p>
        </div>
      </div>
    </section>

    <section className="pp-landing__section" aria-labelledby="pp-evidence-title">
      <div className="pp-landing__wrap pp-landing__split">
        <div><p className="pp-landing__eyebrow">Clarity, even from a distance</p>
          <h2 id="pp-evidence-title">After the ritual</h2>
          <p className="pp-landing__intro">Recorded photo and video documentation is the baseline. Antaryatra can provide the following to your family privately:</p>
          <ul className="pp-landing__evidence">{completionEvidence.map(item => <li key={item}>{item}</li>)}</ul>
        </div>
        <div className="pp-landing__notes">
          <div><h3>A glimpse, when conditions allow</h3><p>Where connectivity and ritual conditions permit, a short WhatsApp video glimpse may be arranged. Live video is not guaranteed.</p></div>
          <div><h3>See what was prepared</h3><p>Antaryatra will make the agreed ritual arrangements. The materials arranged for the agreed ritual can be documented before or during the ceremony so the family can see what was prepared.</p></div>
        </div>
      </div>
    </section>

    <section className="pp-landing__section pp-landing__faq" aria-labelledby="pp-faq-title">
      <div className="pp-landing__wrap pp-landing__split">
        <div><p className="pp-landing__eyebrow">A few things you may be wondering</p><h2 id="pp-faq-title">Questions from families abroad</h2></div>
        <div>{faqs.map(faq => <details key={faq.question}><summary>{faq.question}</summary><p>{faq.answer}</p></details>)}</div>
      </div>
    </section>

    <section className="pp-landing__section pp-landing__closing" aria-labelledby="pp-final-title">
      <div className="pp-landing__wrap"><p className="pp-landing__eyebrow">Begin with a conversation</p>
        <h2 id="pp-final-title">You don’t have to work it all out alone.</h2>
        <p>Tell us what your family is considering. We’ll start with your questions, not a booking.</p>
        {cta('final')}
      </div>
    </section>
  </article>;
}
